/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { Dimensions, View } from "react-native";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from "@material-ui/core";
import moment from "moment";

import { Text } from "UpduoLibrary";
import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { formattedDateString } from "config/Utilities";
import BreakdownRow from "./BreakdownRow";
import {
  skaleBlack,
  skaleMainBlue,
  skaleWhite,
  upduoMediumLightGray,
} from "config/UpduoColors";

const windowHeight = Dimensions.get("window").height;

export default function Breakdown() {
  const timeSeriesData = useSelector(
    (state: RootState) => state.timeSeriesData
  );
  const fullDataQueryType = useSelector(
    (state: RootState) => state.fullDataQueryType
  );
  const dayRangeStart = useSelector((state: RootState) => state.dayRangeStart)!;
  const dayRangeEnd = useSelector((state: RootState) => state.dayRangeEnd)!;

  const [numberOfDates, setNumberOfDates] = useState(0);
  const [dateStringArray, setDateStringArray] = useState<string[]>([]);
  const [displayDateArray, setDisplayDateArray] = useState<string[]>([]);

  useEffect(() => {
    _handleRefreshTable();
  }, [dayRangeEnd, dayRangeStart]);

  const _handleRefreshTable = () => {
    const isWithinOneDay =
      dayRangeEnd.getTime() - dayRangeStart.getTime() < 86400000; // 1 day is 86400000 milliseconds
    const a = new Date(dayRangeStart);
    if (isWithinOneDay) {
      a.setDate(a.getDate() - 1);
    }

    const b = moment(dayRangeEnd);
    const numberOfDates = b.diff(a, "days") + 1; // adding 1 to include endpoint date
    const countEndDate = dayRangeEnd; // today's date
    let countNextDate: Date = countEndDate;
    const dateStringArray: string[] = [];
    const displayDateArray: string[] = [];
    for (let k = 0; k < numberOfDates; k++) {
      countNextDate = new Date(
        countEndDate.getTime() - 1000 * 60 * 60 * 24 * k
      );
      dateStringArray.push(formattedDateString(countNextDate));
      displayDateArray.push(moment(countNextDate).format("ddd, MMM D"));
    }

    setDateStringArray(dateStringArray);
    setDisplayDateArray(displayDateArray);
    setNumberOfDates(numberOfDates);
  };

  const _createDataArray = (data) => {
    const rowDataArray = new Array(numberOfDates).fill(0);
    for (let i = 0; i < data.length; i++) {
      const workingDateString = data[i].date.split("T")[0];
      const dateIndex = dateStringArray.indexOf(workingDateString);
      rowDataArray[dateIndex] = data[i].value;
    }
    return rowDataArray;
  };

  const tableHeaderColumns = displayDateArray.map((item, itemIndex) => (
    <TableCell
      key={itemIndex + 3}
      style={{
        fontWeight: "bold",
        color: skaleBlack,
        backgroundColor: skaleWhite,
      }}
    >
      <View
        style={{
          justifyContent: "center",
          alignItems: "center",
          height: 40,
        }}
      >
        <Text
          style={{
            color: upduoMediumLightGray,
            display: "flex",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {item}
        </Text>
      </View>
    </TableCell>
  ));

  const dataSeries = timeSeriesData[fullDataQueryType || "session_count"];

  tableHeaderColumns.unshift(
    <TableCell
      key={2}
      style={{
        fontWeight: "bold",
        color: skaleMainBlue,
        backgroundColor: skaleWhite,
        height: 40,
      }}
    >
      <Text
        style={{
          color: upduoMediumLightGray,
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        {`Total Credited ${dataSeries?.data_description}`}
      </Text>
    </TableCell>
  );

  // TODO: what should the "breakdown" tab look like when no graph is selected?
  let dataTypeString = dataSeries.data_type;
  dataTypeString = dataTypeString[0].toUpperCase() + dataTypeString.slice(1);
  tableHeaderColumns.unshift(
    <TableCell
      key={1}
      style={{
        flex: 2,
        fontWeight: "bold",
        color: skaleBlack,
        background: skaleWhite,
        height: 40,
      }}
    >
      <Text
        style={{
          color: upduoMediumLightGray,
          fontSize: 14,
        }}
      >
        {dataTypeString}
      </Text>
    </TableCell>
  );

  tableHeaderColumns.unshift(
    <TableCell key={0} style={{ background: skaleWhite }} />
  );

  const breakdownRows = dataSeries.data_sets.map((item, itemIndex) => (
    <BreakdownRow
      key={itemIndex}
      rowIndex={itemIndex}
      item={item}
      fullDataQueryType={fullDataQueryType}
      itemType={dataSeries["data_type"]}
      data={_createDataArray(item.data)}
    />
  ));

  return (
    <View
      style={{
        display: "flex",
        alignItems: "center",
        backgroundColor: skaleWhite,
      }}
    >
      <View style={{ width: "96%" }}>
        <TableContainer style={{ maxHeight: windowHeight }}>
          <Table stickyHeader aria-label={"sticky table"}>
            <TableHead>
              <TableRow hover={true} style={{ flexDirection: "row" }}>
                {tableHeaderColumns}
              </TableRow>
            </TableHead>
            <TableBody>{breakdownRows}</TableBody>
          </Table>
        </TableContainer>
      </View>
    </View>
  );
}
